/* eslint-disable object-shorthand */
/* eslint-disable func-names */
/* eslint-disable react/no-this-in-sfc */
import { useQuery } from "@apollo/client";
import BoxedChartHeader from "components/charts/modern/abstract/BoxedChartHeader";
import HighchartsChart from "components/charts/modern/abstract/HighchartsChart";
import afterSetExtremes from "components/charts/modern/highcharts/afterSetExtremes";
import navigatorStyles from "components/charts/modern/highcharts/navigatorStyles";
import type { AxisScaleType } from "components/charts/modern/inputs/AxisScaleSelector";
import { LogLinearScaleSelector } from "components/charts/modern/inputs/AxisScaleSelector";
import { AreaBarPlotTypeSelector } from "components/charts/modern/inputs/PlotTypeSelector";
import TickerSymbolSelector from "components/charts/modern/inputs/TickerSymbolSelector";
import useRootIssuer from "components/charts/modern/overlay/useRootIssuer";
import KeyValueTooltip from "components/charts/modern/tooltip/KeyValueTooltip";
import OnlyPremiumRender from "components/conversion/OnlyPremiumRender";
import SadFaceIcon from "components/icons/SadFaceIcon";
import LoadingSkeleton from "components/pages/TemplatesGalleryPage/LoadingSkeleton";
import ContentContainerBox from "components/stocks/ContentContainerBox";
import aeonik from "components/utils/aeonik";
import compareAsc from "date-fns/compareAsc";
import lightFormat from "date-fns/lightFormat";
import parseISO from "date-fns/parseISO";
import type Highcharts from "highcharts/highstock";
import type { SeriesAreaOptions, SeriesBarOptions } from "highcharts/highstock";
import SharesHistoryQuery from "lib/graphql/queries/SharesHistoryQuery";
import type {
  ISharesHistoryQuery,
  ISharesHistoryQueryVariables,
} from "lib/graphql/queries/__generated__/SharesHistoryQuery.generated";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import { useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { FormattedMessage, useIntl } from "react-intl";
import colors from "tailwindcss/colors";

interface SharesChartProps {
  tickerSymbols: string[];
  availableTickerSymbols?: string[];
  onToggleTickerSymbols?: (tickerSymbol: string[]) => void;
}

export default function SharesChart({
  tickerSymbols,
  availableTickerSymbols,
  onToggleTickerSymbols,
}: SharesChartProps) {
  const intl = useIntl();
  const issuer = useRootIssuer();
  const isRealEstateFund = issuer.__typename === "BrazilianRealEstateFund";
  const [plotType, setPlotType] = useState<"area" | "column">("area");
  const [axisScaleType, setAxisScaleType] = useState<AxisScaleType>("linear");
  const tickerSymbolsForSeries = [...tickerSymbols];
  const selectedTickerSymbol = tickerSymbolsForSeries[0];
  if (!selectedTickerSymbol) {
    throw new Error("Couldn't locate the selected ticker symbol");
  }
  const { data, previousData, loading } = useQuery<ISharesHistoryQuery, ISharesHistoryQueryVariables>(
    SharesHistoryQuery,
    {
      variables: {
        tickerSymbol: tickerSymbols[0],
      },
    },
  );

  const sharesHistory = (loading && !data ? previousData?.sharesHistory : data?.sharesHistory) || [];
  const hasSharesHistory = sharesHistory.length > 0;

  const series: (SeriesAreaOptions | SeriesBarOptions)[] = [
    {
      showInNavigator: true,
      type: plotType === "area" ? "area" : "bar",
      animation: false,
      name: selectedTickerSymbol,
      data: sharesHistory
        .filter((node) => node.totalSharesOutstanding !== null)
        .map((node) => ({
          x: parseISO(node.asOfDate).getTime(),
          y: node.totalSharesOutstanding,
          custom: node,
        }))
        .sort((a, b) => compareAsc(a.x, b.x)),
      custom: {
        tickerSymbol: selectedTickerSymbol,
        primaryColor: colors.sky[50],
        secondaryColor: colors.sky[600],
      },
    },
  ];

  const minValues = sharesHistory.map((node) => node.totalSharesOutstanding).filter(notUndefinedOrNull);
  const minValue = minValues.length > 0 ? Math.min(...minValues) : 0;

  const labels = {
    sharesOutstanding: isRealEstateFund
      ? intl.formatMessage({
          defaultMessage: "Cotas Emitidas",
          id: "agUdo7",
        })
      : intl.formatMessage({
          defaultMessage: "Ações em Circulação",
          id: "psT3CN",
        }),
    buyback: intl.formatMessage({
      defaultMessage: "Recompra",
      id: "f8wqYD",
    }),
  };
  const options: Highcharts.Options = {
    title: {
      text: "",
    },
    credits: {
      enabled: false,
    },
    exporting: {
      enabled: false,
    },
    chart: {
      className: "font-aeonik font-normal",
      backgroundColor: "transparent",
      panning: {
        enabled: false,
      },
      alignTicks: true,
      zooming: {
        mouseWheel: false,
        singleTouch: false,
      },
      borderWidth: 0,
      height: null,
      plotBorderWidth: 1,
      plotBorderColor: colors.slate[100],
    },
    scrollbar: {
      enabled: false,
    },
    xAxis: {
      zoomEnabled: false,
      gridLineWidth: 1,
      gridLineColor: colors.slate[100],
      lineWidth: 1,
      lineColor: colors.slate[100],
      tickWidth: 0,
      scrollbar: {
        enabled: false,
      },
      labels: {
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
      },
      events: {
        afterSetExtremes: function (this, event) {
          afterSetExtremes(this, event);
        },
      },
    },
    yAxis: {
      type: axisScaleType,
      min: minValue,
      gridLineColor: colors.slate[100],
      lineWidth: 1,
      lineColor: colors.slate[100],
      labels: {
        enabled: true,
        align: "center",
        style: {
          color: colors.slate[500],
          fontFamily: aeonik.style.fontFamily,
          fontWeight: "500",
          fontSize: "12px",
        },
        formatter: function () {
          return intl.formatNumber(parseFloat(String(this.value)), {
            notation: "compact",
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
            minimumSignificantDigits: 1,
          });
        },
      },
      tickWidth: 0,
      showFirstLabel: false,
      showLastLabel: false,
      scrollbar: {
        enabled: false,
      },
    },
    rangeSelector: {
      enabled: false,
    },
    navigator: {
      ...navigatorStyles,
      yAxis: {
        ...navigatorStyles.yAxis,
        events: {
          afterSetExtremes(this, event) {
            this.setExtremes(event.dataMin, event.dataMax);
          },
        },
        min: minValue,
        type: axisScaleType,
      },
    },
    series: [...series],
    tooltip: {
      followPointer: true,
      followTouchMove: true,
      enabled: true,
      shadow: {
        color: "rgba(0, 0, 0, 0.1)",
      },
      borderColor: colors.slate[200],
      borderWidth: 1,
      padding: 0,
      valueDecimals: 2,
      borderRadius: 12,
      shape: "callout",
      useHTML: true,
      split: false,
      shared: true,
      formatter(this: Highcharts.TooltipFormatterContextObject) {
        const node = (this.point as any).custom as (typeof sharesHistory)[number] | null | undefined;
        if (!this.key || !node || node.__typename !== "SharesHistoryItem") {
          return "";
        }
        const valueFormatter = new Intl.NumberFormat(intl.locale, {
          notation: "compact",
          compactDisplay: "short",
          minimumSignificantDigits: 1,
          maximumSignificantDigits: 4,
        });
        return renderToStaticMarkup(
          <KeyValueTooltip
            x={lightFormat(parseISO(node.asOfDate), "dd/MM/yyyy")}
            keyValues={[
              {
                key: labels.sharesOutstanding,
                value: valueFormatter.format(node.totalSharesOutstanding),
                color: colors.orange[500],
              },
            ]}
          />,
        );
      },
    },
    plotOptions: {
      series: {
        enableMouseTracking: true,
        states: {
          hover: {
            borderColor: colors.orange[500],
            color: colors.orange[50],
            lineWidth: 2,
          },
        },
      },
      area: {
        color: colors.orange[50],
        lineColor: colors.orange[600],
        lineWidth: 2,
        marker: {
          enabled: false,
          fillColor: colors.orange[50],
          lineColor: colors.orange[600],
          lineWidth: 2,
          width: 8,
          height: 8,
        },
      },
      bar: {
        color: colors.orange[300],
        borderWidth: 1.5,
        borderColor: colors.orange[300],
        states: {
          hover: {
            borderColor: colors.orange[600],
            color: colors.orange[600],
          },
        },
      },
    },
  };

  if (loading && !hasSharesHistory) {
    return <LoadingSkeleton count={1} className="h-96" />;
  }

  return (
    <ContentContainerBox className="space-y-6">
      <BoxedChartHeader
        paywallDisplayOrigin="Shares chart"
        tickerSymbol={tickerSymbols[0]}
        title={
          isRealEstateFund ? (
            <FormattedMessage defaultMessage="Cotas Emitidas" id="agUdo7" />
          ) : (
            <FormattedMessage defaultMessage="Ações em Circulação" id="psT3CN" />
          )
        }
        subtitle={
          isRealEstateFund ? (
            <FormattedMessage defaultMessage="Gráfico das emissões de cotas no longo prazo." id="4TATJM" />
          ) : (
            <FormattedMessage
              defaultMessage="Indispensável para visualizar o efeito das recompras/diluição no longo prazo."
              id="N6PYEI"
            />
          )
        }
      />

      <OnlyPremiumRender>
        <div className="space-y-4">
          <div className="flex flex-col items-center justify-between sm:flex-row">
            <div className="flex w-full flex-col items-center space-y-4 md:flex-row md:space-x-4 md:space-y-0">
              <AreaBarPlotTypeSelector value={plotType} onChange={setPlotType} />
              <LogLinearScaleSelector value={axisScaleType} onChange={setAxisScaleType} />

              {onToggleTickerSymbols && availableTickerSymbols && availableTickerSymbols.length >= 2 && (
                <TickerSymbolSelector
                  tickerSymbols={availableTickerSymbols}
                  value={tickerSymbols}
                  onChange={(tickerSymbols) => onToggleTickerSymbols(tickerSymbols)}
                />
              )}
            </div>
          </div>

          {hasSharesHistory ? (
            <HighchartsChart options={options} className="h-96 w-80 sm:w-full" />
          ) : (
            <div className="flex h-96 w-80 items-center justify-center sm:w-full">
              <div className="space-y-2 text-center text-base tracking-wide text-gray-600">
                <div>
                  <SadFaceIcon />
                </div>

                <p>
                  <FormattedMessage defaultMessage="Nenhum dado foi encontrado." id="i1Elih" />
                </p>
              </div>
            </div>
          )}
        </div>
      </OnlyPremiumRender>
    </ContentContainerBox>
  );
}
